import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Grid, Cell } from "react-md";
import CaseAttributesCard from "components/QualityAssurance/Sections/CaseDetails/CaseAttributesCard.tsx";
import GPSLocation from "components/QualityAssurance/Sections/CaseDetails/GPSLocation";
import PublicPropertyRecord from "components/Homeowner/CaseForms/PublicPropertyRecord";
import { getGpsData } from "requests/qualityAssurance/index.ts";
import CaseStepper from "components/Homeowner/CaseStepper";
import { STATUS_TRACKER } from "components/Homeowner/CaseStepper/StepTypes";
import DataComparison from "components/QualityAssurance/Sections/Outline/DataComparison";
import { GetDisplayFieldValue } from "components/Shared/Formatters";
import CaseHazards from "components/QualityAssurance/Sections/Outline/CaseHazards";
import { getCaseLinksRedux, getCaseLinksLimitedRedux } from "requests/homeowner/ho/index";
import PropertyCaseHistory from "components/Homeowner/CaseForms/PropertyCaseHistory";
import { CASE_PROPERTY_HISTORY_VIEW, CASE_PROPERTY_HISTORY_VIEW_LIMITED } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import usePhoenixFormsSaveComplete from "components/PhoenixFormsV4/usePhoenixFormsSaveComplete.ts";
import { getCasePhotos } from "requests/photos/index.ts";
import { MSpinner } from "components/Shared";
import ComparisonChart from "components/QualityAssurance/Sections/Outline/ComparisonChart.tsx";

const CaseDetails = (props) => {
  const mounted = useRef(true);
  const [gpsData, setGpsData] = useState({ needsFetch: true, geoCoords: { latitude: 0, longitude: 0 } });
  const [photos, setPhotos] = useState([]);

  const getPhotos = () => {
    getCasePhotos(props.qaCase.caseInfo.customerNumber, props.controlNumber, props.ssoToken, (p) => {
      setPhotos(p);
    });
  };

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!props.controlNumber) return; // how does this happen?

    if (gpsData.needsFetch) {
      fetchGpsData();
    }

    getPhotos();

    if (props.canViewPropertyHistory) {
      props.getCaseLinks(props.controlNumber);
    } else if (props.canViewCasePropertyHistoryLimited) {
      props.getCaseLinksLimited(props.controlNumber);
    }
  }, [props.controlNumber]);

  usePhoenixFormsSaveComplete(() => {
    getPhotos();
    setGpsData({
      needsFetch : true,
      ...gpsData
    });
    fetchGpsData();
  });

  const fetchGpsData = () => {
    getGpsData(props.controlNumber, (s) => {
      setGpsData(s);
    }, mounted);
  };

  if (!photos || photos.length < 1)
    return <MSpinner />;

  const showPropertyHistoryCard = props.canViewPropertyHistory || props.canViewCasePropertyHistoryLimited;

  const {
    controlNumber,
    qaCase: {
      caseInfo: {
        surveyAddress,
        showCaseAttributes,
      },
    },
    displayAddressMap,
    caseStepper,
    caseHeader,
    caseLinks,
    fetchingCaseLinks,
  } = props;

  return (
    <Grid>
      <Cell size={12}>
        <CaseHazards controlNumber={controlNumber} />
      </Cell>
      {props.qaCase?.caseInfo?.hasComparisonFields &&
          <Cell size={12}>
            <ComparisonChart controlNumber={controlNumber} />
          </Cell>}
      <Cell size={12}>
        <PublicPropertyRecord controlNumber={controlNumber} />
      </Cell>
      <Cell size={12}>
        <GPSLocation
          gpsData={gpsData}
          photos={photos}
          address={surveyAddress}
          controlNumber={controlNumber}
          refresh={fetchGpsData}
          onAddressClick={displayAddressMap}
        />
      </Cell>
      <Cell size={12}>
        <DataComparison controlNumber={controlNumber} propertyAddress={{ address: GetDisplayFieldValue(caseHeader.displayFields, "SurveyAddress") }} photos={photos}/>
      </Cell>
      {showCaseAttributes &&
        <Cell size={12}>
          <CaseAttributesCard controlNumber={controlNumber} gridHeightPixels={500} />
        </Cell>
      }
      {(showPropertyHistoryCard && (fetchingCaseLinks || !!caseLinks.length)) &&
        <Cell size={12}>
            <PropertyCaseHistory caseLinks={caseLinks} fetchingCaseLinks={fetchingCaseLinks} />
        </Cell>
      }
      <Cell size={12} className={!caseStepper.trackerActive ? "case-stepper-disabled" : ""}>
        <CaseStepper controlNumber={controlNumber} stepType={STATUS_TRACKER} />
      </Cell>
    </Grid>
  );
};

CaseDetails.propTypes = {
  controlNumber: PropTypes.number.isRequired,
  qaCase: PropTypes.object.isRequired,
  photos: PropTypes.object,
  displayAddressMap: PropTypes.func,
  caseStepper: PropTypes.object,
  caseHeader: PropTypes.isRequired,
  getCaseLinks: PropTypes.func,
  getCaseLinksLimited: PropTypes.func,
  caseLinks: PropTypes.array,
  fetchingCaseLinks: PropTypes.bool,
  canViewPropertyHistory: PropTypes.bool,
  canViewCasePropertyHistoryLimited: PropTypes.bool,
  ssoToken: PropTypes.string,
};

const mapStateToProps = state => {
  return {
    qaCase: state.qualityAssurance.qaCase,
    caseStepper: state.caseStepper,
    caseHeader: state.caseForms.caseHeader,
    caseLinks: state.caseLinks.caseLinks,
    fetchingCaseLinks: state.caseLinks.fetchingCaseLinks,
    canViewPropertyHistory: userHasRight(CASE_PROPERTY_HISTORY_VIEW)(state),
    canViewCasePropertyHistoryLimited: userHasRight(CASE_PROPERTY_HISTORY_VIEW_LIMITED)(state),
    ssoToken: state.user.profile.token,
  };
};

const mapDispatchToProps = dispatch => ({
  getCaseLinks(controlNumber) {
    dispatch(getCaseLinksRedux(controlNumber));
  },
  getCaseLinksLimited(controlNumber) {
    dispatch(getCaseLinksLimitedRedux(controlNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CaseDetails);
